/**
 * Web Push, the real kind.
 *
 * This is a standards-based VAPID subscription against the browser's own push
 * service, delivered through the service worker — so a notification arrives
 * when the app is closed, which is the entire point. Nothing here shows a
 * notification while the page is open and calls it push.
 *
 * The honest constraints, which the interface has to reflect rather than hide:
 *
 *   • Only the person holding the device can grant permission. Admin can
 *     decide who *should* be notified; Admin cannot grant it for them.
 *   • Permission, once denied, cannot be asked for again from script. The only
 *     route back is the browser's own settings, so the app has to say so.
 *   • On iOS, push works only from an app installed to the Home Screen. In a
 *     Safari tab the API is simply absent, and prompting is pointless.
 */

import { registerPushSubscription, unregisterPushSubscription } from '../data/callables';
import { isIOS, isStandalone } from './serviceWorker';

export type PushState =
  /** The browser has no push support at all. */
  | 'unsupported'
  /** iOS, in a browser tab. Install to the Home Screen first. */
  | 'needs-install'
  /** Supported and available, not yet asked. */
  | 'prompt'
  /** Granted and subscribed on this device. */
  | 'subscribed'
  /** Granted, but no subscription on this device yet. */
  | 'granted-not-subscribed'
  /** Refused. Only the browser's settings can undo this. */
  | 'denied';

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Where this device stands right now. */
export async function currentPushState(): Promise<PushState> {
  // iOS exposes the Push API only to installed apps, so the answer for a tab
  // is "install first" rather than "unsupported" — a meaningful difference.
  if (isIOS() && !isStandalone()) return 'needs-install';
  if (!pushSupported()) return 'unsupported';

  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'default') return 'prompt';

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  return existing ? 'subscribed' : 'granted-not-subscribed';
}

/**
 * Ask, subscribe, and register the result with the server.
 *
 * Must be called from a user gesture: browsers ignore — and Safari penalises —
 * a permission prompt that no tap asked for.
 */
export async function enablePush(): Promise<PushState> {
  /*
   * The permission request must come first, before anything is awaited.
   *
   * Safari grants a click a short-lived "user activation", and awaiting
   * *anything* — even `navigator.serviceWorker.ready`, which usually resolves
   * immediately — can spend it. Once it is gone, `requestPermission()` is
   * ignored: no prompt appears, no error is thrown, and the promise resolves
   * to "default" as though the user had dismissed a dialog they were never
   * shown. This was exactly the iPhone bug.
   *
   * So every check below is synchronous, and the request happens in the same
   * turn as the tap. The service worker and the subscription are dealt with
   * afterwards, when losing activation no longer matters.
   */
  if (isIOS() && !isStandalone()) return 'needs-install';
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';

  const permission =
    Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'prompt';

  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      // Non-negotiable in Chrome, and correct everywhere: every message this
      // app sends is one the reader is meant to see.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(import.meta.env.VITE_VAPID_PUBLIC_KEY),
    }));

  const json = subscription.toJSON();
  await registerPushSubscription({
    endpoint: subscription.endpoint,
    p256dh: json.keys?.p256dh ?? '',
    auth: json.keys?.auth ?? '',
    device: describeDevice(),
  });

  return 'subscribed';
}

/**
 * Unsubscribe this device.
 *
 * Both halves, in this order: the server record first, so a failure leaves a
 * subscription that still works rather than a live endpoint the server has
 * forgotten and will never clean up.
 */
export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  await unregisterPushSubscription({ endpoint: subscription.endpoint }).catch(() => {});
  await subscription.unsubscribe().catch(() => {});
}

/**
 * Re-register the current subscription.
 *
 * Push services rotate endpoints, and a subscription can be replaced without
 * the app noticing. Called on sign-in so the server's copy stays the live one;
 * the registration is an upsert keyed on the endpoint, so repeating it is free.
 */
export async function refreshPushRegistration(): Promise<void> {
  if (!pushSupported() || Notification.permission !== 'granted') return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    const json = subscription.toJSON();
    await registerPushSubscription({
      endpoint: subscription.endpoint,
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
      device: describeDevice(),
    });
  } catch {
    // Best effort. A stale registration produces a failed delivery that the
    // server cleans up on its own.
  }
}

/**
 * A coarse device label for the settings screen.
 *
 * "iPhone · Safari", not the user-agent string. Enough to tell two devices
 * apart when revoking one; not a fingerprint.
 */
function describeDevice(): string {
  const ua = navigator.userAgent;
  const platform = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Macintosh/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : 'Device';

  const browser = /EdgA?\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua) && !/Chromium/.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';

  return `${platform} · ${browser}`;
}

/**
 * The VAPID public key, as the PushManager wants it.
 *
 * It is published as base64url; `applicationServerKey` takes raw bytes. The
 * padding and the two substitutions are the whole conversion.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalised);
  // Backed by a plain ArrayBuffer, which is what BufferSource requires — a
  // bare `new Uint8Array(n)` is typed as possibly SharedArrayBuffer-backed.
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

/** Wording for each state. Kept here so every surface says the same thing. */
export const PUSH_COPY: Record<PushState, { title: string; body: string }> = {
  unsupported: {
    title: 'Not available in this browser',
    body: 'This browser does not support push notifications. Email still works.',
  },
  'needs-install': {
    title: 'Add SamuelSchool to your Home Screen first',
    body: 'On iPhone, notifications only work from the installed app. Tap Share, then “Add to Home Screen”, and open it from there.',
  },
  prompt: {
    title: 'Get notified',
    body: 'Announcements and new files arrive as notifications, even when the app is closed.',
  },
  subscribed: {
    title: 'Notifications are on',
    body: 'This device will receive announcements and updates.',
  },
  'granted-not-subscribed': {
    title: 'Almost there',
    body: 'Notifications are allowed but this device is not registered yet.',
  },
  denied: {
    title: 'Notifications are blocked',
    body: 'You turned these off for SamuelSchool. Only your browser’s settings can turn them back on — the app cannot ask again.',
  },
};
